import type React from "react";
import { useEffect, useState } from "react";
import toast, { Toaster, type Toast as ToastType } from "react-hot-toast";

/**
 * Toast notification types
 */
export type ToastVariant = "success" | "error" | "warning" | "info" | "loading";

/**
 * Toast configuration options
 */
export interface ToastOptions {
    duration?: number;
    position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
    icon?: React.ReactElement | string;
    id?: string;
}

/**
 * Default toast configuration
 */
const DEFAULT_TOAST_OPTIONS: ToastOptions = {
    duration: 4000,
    position: "top-center",
};

/**
 * Progress bar component - animates from left to right
 */
const ToastProgressBar: React.FC<{
    duration: number;
    variant: ToastVariant;
}> = ({ duration, variant }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const interval = 50; // Update every 50ms
        const increment = (interval / duration) * 100;

        const timer = setInterval(() => {
            setProgress((prev) => {
                const next = prev + increment;
                return next >= 100 ? 100 : next;
            });
        }, interval);

        return () => clearInterval(timer);
    }, [duration]);

    const getProgressColor = () => {
        switch (variant) {
            case "success":
                return "bg-green-500";
            case "error":
                return "bg-red-500";
            case "warning":
                return "bg-yellow-500";
            case "info":
                return "bg-blue-600";
            case "loading":
                return "bg-gray-500";
            default:
                return "bg-gray-500";
        }
    };

    return (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
            <div
                className={`h-full transition-all duration-50 ease-linear ${getProgressColor()}`}
                style={{ width: `${progress}%` }}
            />
        </div>
    );
};

/**
 * Custom toast wrapper with progress bar
 */
const CustomToast: React.FC<{
    t: ToastType;
    message: string;
    variant: ToastVariant;
}> = ({ t, message, variant }) => {
    return (
        <div className="relative">
            <div className="flex items-center gap-2">
                {t.icon && <span>{t.icon}</span>}
                <span>{message}</span>
            </div>
            {t.duration && t.duration > 0 && <ToastProgressBar duration={t.duration} variant={variant} />}
        </div>
    );
};

/**
 * Toast notification utility with type-safe methods
 */
export const Toast = {
    /**
     * Show success toast
     */
    success: (message: string, options?: ToastOptions): string => {
        return toast.custom(
            (t) => (
                <div className="toast--success">
                    <CustomToast t={t} message={message} variant="success" />
                </div>
            ),
            {
                ...DEFAULT_TOAST_OPTIONS,
                ...options,
                duration: options?.duration ?? 3000,
            },
        );
    },

    /**
     * Show error toast
     */
    error: (message: string, options?: ToastOptions): string => {
        return toast.custom(
            (t) => (
                <div className="toast--error">
                    <CustomToast t={t} message={message} variant="error" />
                </div>
            ),
            {
                ...DEFAULT_TOAST_OPTIONS,
                duration: 5000,
                ...options,
            },
        );
    },

    /**
     * Show warning toast
     */
    warning: (message: string, options?: ToastOptions): string => {
        return toast.custom(
            (t) => (
                <div className="toast--warning">
                    <CustomToast t={t} message={message} variant="warning" />
                </div>
            ),
            {
                ...DEFAULT_TOAST_OPTIONS,
                ...options,
            },
        );
    },

    /**
     * Show info toast
     */
    info: (message: string, options?: ToastOptions): string => {
        return toast.custom(
            (t) => (
                <div className="toast--info">
                    <CustomToast t={t} message={message} variant="info" />
                </div>
            ),
            {
                ...DEFAULT_TOAST_OPTIONS,
                ...options,
            },
        );
    },

    /**
     * Show loading toast (persists until dismissed)
     */
    loading: (message: string, options?: ToastOptions): string => {
        const merged = {
            ...DEFAULT_TOAST_OPTIONS,
            ...options,
        } as unknown as Partial<
            Pick<
                ToastType,
                | "style"
                | "className"
                | "id"
                | "icon"
                | "duration"
                | "ariaProps"
                | "position"
                | "iconTheme"
                | "toasterId"
                | "removeDelay"
            >
        >;

        return toast.loading(message, merged as unknown as Parameters<typeof toast.loading>[1]);
    },

    /**
     * Dismiss a specific toast by ID
     */
    dismiss: (toastId?: string): void => {
        toast.dismiss(toastId);
    },

    /**
     * Dismiss all toasts
     */
    dismissAll: (): void => {
        toast.dismiss();
    },

    /**
     * Update an existing toast
     */
    update: (toastId: string, message: string, variant: ToastVariant = "info"): void => {
        const updateOptions = {
            id: toastId,
        };

        switch (variant) {
            case "success":
                toast.success(message, updateOptions);
                break;
            case "error":
                toast.error(message, updateOptions);
                break;
            case "loading":
                toast.loading(message, updateOptions);
                break;
            default:
                toast(message, updateOptions);
        }
    },

    /**
     * Show a promise-based toast (auto updates on resolve/reject)
     */
    promise: <T,>(
        promise: Promise<T>,
        messages: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: Error) => string);
        },
        options?: ToastOptions,
    ): Promise<T> => {
        return toast.promise(promise, messages, {
            ...DEFAULT_TOAST_OPTIONS,
            ...options,
        });
    },
};

/**
 * ToastContainer component - add to your app root
 */
export const ToastContainer: React.FC = () => {
    return (
        <Toaster
            data-testid="toast-component"
            position="top-right"
            reverseOrder={false}
            gutter={8}
            containerClassName=""
            containerStyle={{}}
            toastOptions={{
                className: "",
                duration: 4000,
                style: {
                    background: "transparent",
                    boxShadow: "none",
                    padding: 0,
                },
                success: {
                    duration: 3000,
                },
                error: {
                    duration: 5000,
                },
            }}
        />
    );
};

/**
 * Hook to access toast utilities
 */
export const useToast = () => {
    return Toast;
};

export default Toast;
